/**
 * AutomationsSettingsPage — manage fias_leads_automation_rules.
 * Trigger + action are free-text strings in v1; the executor runtime
 * matches them against an allowlist (e.g. trigger 'contact_created' →
 * action 'create_task' / 'send_template_email'). Executions log shown
 * inline per rule when expanded.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsAutomations,
  useCreateFiasLeadsAutomation,
  useToggleFiasLeadsAutomation,
  useDeleteFiasLeadsAutomation,
  useFiasLeadsAutomationExecutions,
} from '../hooks/useFiasLeads';
import { SkeletonBlock } from '../components/Skeletons';
import { useConfirm } from '@/components/ui/confirm-dialog';

interface Props {
  holderId: string;
}

const TRIGGER_TYPES = [
  'contact_created',
  'contact_updated',
  'deal_created',
  'deal_moved_to_stage',
  'deal_won',
  'deal_lost',
  'activity_logged',
];

const ACTION_TYPES = [
  'create_task',
  'send_template_email',
  'set_lifecycle_stage',
  'add_tag',
  'log_activity',
];

export function AutomationsSettingsPage({ holderId }: Props) {
  const confirm = useConfirm();
  const rulesQuery = useFiasLeadsAutomations(holderId);
  const createRule = useCreateFiasLeadsAutomation(holderId);
  const toggleRule = useToggleFiasLeadsAutomation(holderId);
  const deleteRule = useDeleteFiasLeadsAutomation(holderId);

  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    triggerType: TRIGGER_TYPES[0],
    actionType: ACTION_TYPES[0],
  });
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    await createRule.mutateAsync({
      name: draft.name.trim(),
      triggerType: draft.triggerType,
      actionType: draft.actionType,
    });
    setDraft({ name: '', triggerType: TRIGGER_TYPES[0], actionType: ACTION_TYPES[0] });
    setShowCreate(false);
  };

  if (rulesQuery.isLoading) return <SkeletonBlock lines={6} />;

  const rules = rulesQuery.data ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-4">
      <Link
        to={`${ARCHE_PATH}/settings`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Settings
      </Link>

      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Automations</h1>
          <p className="text-sm text-muted-foreground">
            Trigger / action workflows. Executes server-side when conditions match.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
        >
          <Plus className="h-4 w-4" />
          New rule
        </button>
      </header>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="rounded border border-border bg-card p-4 flex flex-col gap-2"
        >
          <input
            type="text"
            placeholder="Rule name *"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            className="rounded border border-border bg-background px-3 py-2 text-sm"
            required
            autoFocus
          />
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Trigger</span>
              <select
                value={draft.triggerType}
                onChange={(e) => setDraft((d) => ({ ...d, triggerType: e.target.value }))}
                className="rounded border border-border bg-background px-3 py-2 text-sm"
              >
                {TRIGGER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Action</span>
              <select
                value={draft.actionType}
                onChange={(e) => setDraft((d) => ({ ...d, actionType: e.target.value }))}
                className="rounded border border-border bg-background px-3 py-2 text-sm"
              >
                {ACTION_TYPES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!draft.name.trim() || createRule.isPending}
              className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
            >
              {createRule.isPending ? 'Creating…' : 'Create rule'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="rounded border border-border bg-card">
        <ul className="divide-y divide-border">
          {rules.map((r) => (
            <li key={r.id} className="flex flex-col">
              <div className="p-3 flex items-center gap-3 group">
                <button
                  onClick={() => setExpandedRuleId((id) => (id === r.id ? null : r.id))}
                  className="text-muted-foreground"
                >
                  {expandedRuleId === r.id ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.trigger_type} → {r.action_type} · {r.execution_count} run
                    {r.execution_count === 1 ? '' : 's'}
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={r.is_active}
                    onChange={() => toggleRule.mutateAsync(r.id)}
                  />
                  {r.is_active ? 'Active' : 'Inactive'}
                </label>
                <button
                  onClick={async () => {
                    if (
                      await confirm({
                        title: `Delete rule "${r.name}"?`,
                        description: 'Execution history is dropped.',
                        confirmLabel: 'Delete rule',
                        destructive: true,
                      })
                    ) {
                      deleteRule.mutateAsync(r.id);
                    }
                  }}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {expandedRuleId === r.id && <ExecutionsBlock holderId={holderId} ruleId={r.id} />}
            </li>
          ))}
        </ul>
        {rules.length === 0 && (
          <p className="p-6 text-center text-muted-foreground text-sm">No automation rules yet.</p>
        )}
      </div>
    </div>
  );
}

function ExecutionsBlock({ holderId, ruleId }: { holderId: string; ruleId: string }) {
  const executionsQuery = useFiasLeadsAutomationExecutions(holderId, ruleId, 50);
  const executions = executionsQuery.data ?? [];

  if (executionsQuery.isLoading) {
    return <div className="px-12 py-2 text-xs text-muted-foreground">Loading executions…</div>;
  }
  if (executions.length === 0) {
    return <div className="px-12 py-2 text-xs text-muted-foreground">No executions yet.</div>;
  }
  return (
    <div className="px-12 py-2 bg-muted/20">
      <ul className="divide-y divide-border">
        {executions.map((e) => (
          <li key={e.id} className="py-2 text-xs flex items-center gap-2">
            <span
              className={
                'rounded px-2 py-0.5 ' +
                (e.status === 'success'
                  ? 'bg-emerald-100 text-emerald-700'
                  : e.status === 'failure'
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-slate-100 text-slate-700')
              }
            >
              {e.status}
            </span>
            <span className="text-muted-foreground">
              {e.trigger_entity_type} {e.trigger_entity_id.slice(0, 12)}…
            </span>
            <span className="ml-auto text-muted-foreground">
              {new Date(e.executed_at).toLocaleString()}
            </span>
            {e.error_message && <span className="text-destructive">{e.error_message}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
